<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDemoStore } from '@/stores/demo'
import { useTheme } from '@/composables/useTheme'
import AppLayout from '@/components/layout/AppLayout.vue'
import DemoEmbedLayout from '@/components/layout/DemoEmbedLayout.vue'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import Toast from 'primevue/toast'
import PersistentToast from '@/components/ui/PersistentToast.vue'
import CommandPalette from '@/components/ui/CommandPalette.vue'
import { useNetworkStatus } from '@/composables/useNetworkStatus'
import { useOfflineQueue } from '@/composables/useOfflineQueue'
import { onServiceWorkerUpdate, applyServiceWorkerUpdate } from '@/utils/authServiceWorker'

const authStore = useAuthStore()
const { online } = useNetworkStatus()
const { pendingCount, isFlushing } = useOfflineQueue()
const demoStore = useDemoStore()
const route = useRoute()
const { initTheme } = useTheme()

// Rendered inside an iframe = the landing demo embed. Force memory-only auth
// synchronously, before any hydration, so the iframe can never read or clobber
// the parent tab's real session via shared same-origin localStorage.
if (typeof window !== 'undefined' && window.self !== window.top) {
  authStore.setMemoryOnly(true)
}

// Treat each navigation as demo activity so the idle countdown resets.
watch(() => route.fullPath, () => demoStore.resetActivity())

// Check if current route uses landing layout (no AppLayout wrapper)
const isLandingLayout = computed(() => route.meta.layout === 'landing')

// Embedded landing demo runs in its own slim shell (filtered dock, no account
// chrome). Driven by a per-context flag so only the iframe is affected.
const isEmbeddedDemo = computed(() => demoStore.embedded)

// Show AppLayout for authenticated users on non-landing pages.
// Guest routes (login, set-password) must never show app chrome — guards
// against the dock flashing on /login during the brief authenticated moment
// before a deliverer is signed back out (deliverers are web-blocked).
const showAppLayout = computed(() =>
  authStore.isAuthenticated &&
  !isLandingLayout.value &&
  !isEmbeddedDemo.value &&
  !route.meta.requiresGuest
)

// PWA update prompt. The merged service worker installs a new build in the
// background but waits to take over (registerType: 'prompt'), so we surface a
// banner instead of reloading under the user. Tapping Reload tells the waiting
// worker to activate, then the page reloads onto the fresh shell.
const updateReady = ref(false)
let stopUpdateWatch: (() => void) | null = null

onMounted(() => {
  authStore.initAuth()
  demoStore.restore()
  initTheme()
  stopUpdateWatch = onServiceWorkerUpdate(() => {
    updateReady.value = true
  })
})

onBeforeUnmount(() => {
  stopUpdateWatch?.()
})
</script>

<template>
  <div id="app">
    <div
      v-if="!online || pendingCount > 0"
      class="offline-banner"
      :class="{ 'is-offline': !online, 'is-syncing': online && pendingCount > 0 }"
      role="status"
      aria-live="polite"
    >
      <i :class="[!online ? 'pi pi-wifi' : isFlushing ? 'pi pi-spin pi-spinner' : 'pi pi-cloud-upload']" />
      <span v-if="!online">{{ $t('common.offline') }} — {{ $t('common.queuedForReplay') }}</span>
      <span v-else>{{ $t('common.syncedPending', { count: pendingCount }) }}</span>
    </div>
    <div
      v-if="updateReady"
      class="update-banner"
      role="status"
      aria-live="polite"
    >
      <i class="pi pi-sync" />
      <span>{{ $t('common.updateAvailable') }}</span>
      <button type="button" class="update-banner__action" @click="applyServiceWorkerUpdate">
        {{ $t('common.reload') }}
      </button>
    </div>
    <ErrorBoundary>
      <!-- Embedded landing demo: slim shell, scoped nav -->
      <DemoEmbedLayout v-if="isEmbeddedDemo">
        <RouterView />
      </DemoEmbedLayout>
      <!-- Authenticated app with sidebar/topbar layout -->
      <AppLayout v-else-if="showAppLayout">
        <RouterView />
      </AppLayout>
      <!-- Landing page and unauthenticated views (login, etc.) -->
      <RouterView v-else />
    </ErrorBoundary>
    <Toast />
    <PersistentToast />
    <CommandPalette />
  </div>
</template>

<style>
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: var(--app-font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--app-background);
  color: var(--app-text-primary);
  transition: background-color var(--app-transition-base), color var(--app-transition-base);
  line-height: 1.6;
}

#app {
  min-height: 100vh;
}

/* Sticky offline / sync banner */
.offline-banner {
  position: sticky;
  top: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.875rem;
  padding: 6px 12px;
  color: white;
  text-align: center;
}
.offline-banner.is-offline { background: #b91c1c; }
.offline-banner.is-syncing { background: #2563eb; }

/* PWA "new version" prompt — mirrors the offline banner, brand-coloured. */
.update-banner {
  position: sticky;
  top: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.875rem;
  padding: 6px 12px;
  color: white;
  text-align: center;
  background: var(--app-primary-color);
}
.update-banner__action {
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  color: var(--app-primary-color);
  background: white;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  min-height: 32px;
}
.update-banner__action:hover { opacity: 0.9; }

/* PrimeVue component customizations */
.p-button {
  font-family: inherit;
  transition: all var(--app-transition-fast);
}

.p-card {
  background-color: var(--app-surface-card);
  border: var(--app-card-border);
  border-radius: var(--app-card-border-radius);
  box-shadow: var(--app-shadow-sm);
  transition: all var(--app-transition-base);
}

.p-inputtext,
.p-password input,
.p-dropdown {
  background-color: var(--app-surface-card);
  color: var(--app-text-primary);
  border-color: var(--app-border);
  transition: all var(--app-transition-fast);
}

.p-inputtext:focus,
.p-password input:focus,
.p-dropdown:focus {
  border-color: var(--app-primary-color);
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
}

/* Enhanced focus states for accessibility */
.p-button:focus {
  box-shadow: 0 0 0 2px var(--app-primary-color), 0 0 0 4px rgba(245, 158, 11, 0.15);
}

/* Loading states */
.p-button.p-button-loading {
  opacity: 0.8;
}


/* Responsive text sizes */
@media (max-width: 767px) {
  html {
    font-size: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
